'use client';

import type { URL as URLType } from 'node:url';
import { ArrowUpRight, Envelope, Link as PhosphorLink } from '@phosphor-icons/react/dist/ssr';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import SvanenIcon from '../../shared/svg/other/svanen-icon';
import SkrymIconWithBackground from '../../shared/svg/skrym/skrym-icon-with-background';
import EncoreIcon from '../../shared/svg/systems/encore-icon';
import FigmaIcon from '../../shared/svg/systems/figma-icon';
import GithubIcon from '../../shared/svg/systems/github-icon';
import GoogleCloudIcon from '../../shared/svg/systems/google-cloud-icon';
import GoogleDocsIcon from '../../shared/svg/systems/google-docs-icon';
import GoogleDriveIcon from '../../shared/svg/systems/google-drive-icon';
import GoogleSheetsIcon from '../../shared/svg/systems/google-sheets-icon';
import GoogleSlidesIcon from '../../shared/svg/systems/google-slides-icon';
import KlarnaIcon from '../../shared/svg/systems/klarna-icon';
import WikipediaIcon from '../../shared/svg/systems/wikipedia-icon';
import YoutubeIcon from '../../shared/svg/systems/youtube-icon';

type LinkIconType =
  | 'g-docs'
  | 'g-sheets'
  | 'g-slides'
  | 'g-cloud'
  | 'g-drive'
  | 'github'
  | 'klarna'
  | 'figma'
  | 'wikipedia'
  | 'encore'
  | 'svanen'
  | 'email'
  | 'internal'
  | 'scope'
  | 'youtube'
  | 'skrym.com'
  | undefined;

function isInternalLink(href: string) {
  try {
    const url = new URL(href, location.href);
    return url.origin === location.origin;
  } catch (_error) {
    return false;
  }
}

function parseLinkType(link: string): LinkIconType {
  if (link.includes('docs.google.com/document')) {
    return 'g-docs';
  }
  if (link.includes('sheets.google.com') || link.includes('docs.google.com/spreadsheets')) {
    return 'g-sheets';
  }
  if (link.includes('slides.google.com') || link.includes('docs.google.com/presentation')) {
    return 'g-slides';
  }
  if (link.includes('drive.google.com')) {
    return 'g-drive';
  }
  if (link.includes('cloud.google.com')) {
    return 'g-cloud';
  }
  if (link.includes('github.com')) {
    return 'github';
  }
  if (link.includes('figma.com/file')) {
    return 'figma';
  }
  if (link.includes('youtube')) {
    return 'youtube';
  }
  if (link.includes('wikipedia')) {
    return 'wikipedia';
  }
  if (link.includes('mailto:')) {
    return 'email';
  }
  if (link.includes('encore')) {
    return 'encore';
  }
  if (link.includes('svanen.se')) {
    return 'svanen';
  }
  if (link.includes('klarna.com')) {
    return 'klarna';
  }
  // Skrym link types
  if (isInternalLink(link)) {
    return 'internal';
  }
  if (link.includes('scope.skrym')) {
    return 'scope';
  }
  if (link.includes('skrym.com')) {
    return 'skrym.com';
  }
  return undefined;
}

interface LinkIconProps {
  link: string;
  className?: string;
}

function LinkIcon(props: LinkIconProps) {
  const { link, className } = props;

  const [imageFetchFailed, setImageFetchFailed] = useState(false);

  useEffect(() => {
    setImageFetchFailed(false);
  }, [link]);

  const linkType = parseLinkType(link);

  let url: URLType | undefined;
  try {
    url = new URL(link);
  } catch (_e) {
    url = undefined;
  }

  switch (linkType) {
    case 'g-drive':
      return <GoogleDriveIcon className={className} />;
    case 'g-docs':
      return <GoogleDocsIcon className={className} />;
    case 'g-sheets':
      return <GoogleSheetsIcon className={className} />;
    case 'g-slides':
      return <GoogleSlidesIcon className={className} />;
    case 'g-cloud':
      return <GoogleCloudIcon className={className} />;
    case 'github':
      return <GithubIcon className={className} />;
    case 'figma':
      return <FigmaIcon className={className} />;
    case 'encore':
      return <EncoreIcon className={className} />;
    case 'klarna':
      return <KlarnaIcon className={className} />;
    case 'youtube':
      return <YoutubeIcon className={className} />;
    case 'wikipedia':
      return <WikipediaIcon className={className} />;
    case 'svanen':
      return <SvanenIcon className={className} color="#009a44" />;
    case 'scope':
    case 'skrym.com':
      return <SkrymIconWithBackground className={className} />;
    case 'email':
      return <Envelope weight="bold" className={className} />;
    case 'internal':
      return <ArrowUpRight weight="bold" className={className} />;
  }

  if (url && !imageFetchFailed) {
    return (
      // biome-ignore lint/performance/noImgElement: This is fine here
      <img
        src={`${url.origin}/favicon.ico`}
        alt={`${url.host} favicon`}
        onError={() => setImageFetchFailed(true)}
        className={className}
      />
    );
  }

  return <PhosphorLink weight="bold" className={className} />;
}

interface MentionProps {
  label?: string;
  href: string;
}

/**
    @deprecated use markdown syntax [text](url) instead
*/
function Mention(props: MentionProps) {
  const { label, href } = props;

  const LinkComponent = isInternalLink(href) ? Link : 'a';

  return (
    <LinkComponent
      href={href}
      className="inline-flex items-center gap-1 rounded-lg px-1 cursor-pointer dark:hover:bg-neutral-700 hover:bg-neutral-800/10 align-bottom"
      title={href}
      target={isInternalLink(href) ? '' : '_blank'}
      rel={isInternalLink(href) ? '' : 'noopener noreferrer'}
    >
      <LinkIcon link={href} className="h-4 w-auto m-0 rounded-xs" />
      <span className="font-semibold whitespace-nowrap border-border/25 dark:text-neutral-300">
        {label}
      </span>
    </LinkComponent>
  );
}

export default Mention;
